if ( ! customElements.get('quick-buy') ) {

	class QuickBuy extends HTMLElement {
		
		constructor(){

			super();

			// Track observers and timers so they can be cleaned up if the
			// element is removed via Section Rendering or pagination.
			this._timers = [];
			this._observers = [];

			// Product variant event listener for theme specific logic

			this.zIndex = 99;

			const hasProductItem = this.closest('[data-js-product-item]');

			this.productVariants = this.querySelector('product-variants');

			if ( hasProductItem ) {
				this.variantImages = this.querySelector('[data-js-quick-buy-product-images]');
				this.productImage = this.closest('[data-js-product-item]').querySelector('[data-js-product-item-image]');
				this.variantImagesSliders = this.hasAttribute('data-variant-images-slider');
				this.productSlider = this.closest('[data-js-product-item]').querySelector('css-slider');
			}

			this.productVariants.addEventListener('VARIANT_CHANGE', this.onVariantChangeHandler.bind(this));
			this.onVariantChangeHandler({target:this.productVariants});

			if ( this.variantImages ) {

				this._timers.push(setTimeout(()=>{
					const imageObserver = new IntersectionObserver((entries, observer) => {
						if ( ! entries[0].isIntersecting ) {
							return;
						} else {
							this.variantImages.querySelectorAll(`template`).forEach(elm=>{
								if ( ! elm.dataset.init ) {
									this.initVariantImage(elm);
								}
							});
							observer.unobserve(this);
						}
					});
					this._observers.push(imageObserver);
					imageObserver.observe(this);
				}, 5000));

			}

			this._timers.push(setTimeout(()=>{

				this.querySelectorAll('.product-variant__item--radio').forEach(elm=>{

					elm.addEventListener('click', e=>{

						// The swatch row is a plain scroller, not a css-slider. Guarded so
						// this never throws if neither wrapper is present.
						elm.closest('.product-variant__scroller')?.scrollTo({
							top: 0,
							left: elm.offsetLeft - 40,
							behavior: 'smooth'
						});

						if ( elm.classList.contains('product-variant__item--color' ) ) {
							elm.closest('.product-variant').querySelector('.product-variant__item-text-label').textContent = elm.querySelector('input').value;
						}

					})
				});

			}, 1000));

			this._revealCheckedSwatches();

			// trigger cart popup

			if ( this.querySelector('[data-js-quick-buy-form]') ) {
				// Keyboard activation clicks the submit button with detail 0 (the gift wrap checkbox's scripted click isn't
				// trusted): the drawer then takes focus.
				this.querySelector('form').addEventListener('click', e=>{
					if ( e.target.closest('[type="submit"]') ) this._viaKeyboard = e.isTrusted && e.detail === 0;
				});
				this.querySelector('form').addEventListener('submit', e=>{

					e.preventDefault();

					const submitButton = this.querySelector('[type="submit"]');

					// Sold out, unavailable or not chosen yet: the button is only class-disabled, so Enter/Space still reach here
					if ( submitButton?.classList.contains('disabled') ) return;
					// An add is already on its way: a second Enter or key repeat must not add again
					if ( submitButton?.classList.contains('working') ) return;

					const viaKeyboard = !! this._viaKeyboard;
					submitButton.classList.add('working');

					window.handleAddToCart(
						this._serializeForm(e.currentTarget), 1,
						{
							'Content-Type': 'application/x-www-form-urlencoded',
							'X-Requested-With': 'XMLHttpRequest'
						},
						()=>{
							submitButton.classList.remove('working');
							if ( viaKeyboard ) OrdoUtils.focusCartClose();
						}
					);

				});

			}

			// update product card prices

			this._timers.push(setTimeout(()=>{

				if ( hasProductItem ) {

					// A card can show its price in both the price block and the rating row.
					const cardPrices = this.closest('[data-js-product-item]').querySelectorAll('[data-js-product-item-price]');
					const productPrice = this.querySelector('[data-js-quick-buy-product-price]');

					const priceObserver = new MutationObserver(()=>{
						cardPrices.forEach(cardPrice=>{
							cardPrice.innerHTML = productPrice.innerHTML;
							if ( productPrice.querySelector('[data-js-product-price-original]').textContent !== '' ) {
								cardPrice.parentNode.classList.remove('product-item__price--empty');
							} else {
								cardPrice.parentNode.classList.add('product-item__price--empty');
							}
						});
						this._updateCardBadge();
					});
					priceObserver.observe(productPrice, {
						attributes: false, childList: true, subtree: true
					});
					this._observers.push(priceObserver);

				}

				if ( this.dataset.defaultToFirstVariant === 'true' ) {
					this.querySelector('product-variants').updatePrice();
				}

			}, 10));

		}

		// A move, such as a css-slider wrapping its slides, reconnects in the same task: only a real removal tears down
		disconnectedCallback(){
			queueMicrotask(()=>{
				if ( this.isConnected ) return;
				this._timers.forEach(id => clearTimeout(id));
				this._timers = [];
				this._observers.forEach(o => o.disconnect());
				this._observers = [];
			});
		}

		// The card's own swatch starts in view, however far along its row it sits; once the shopper touches the row, only they scroll it.
		_revealCheckedSwatches(){

			this.querySelectorAll('.product-variant__scroller').forEach(scroller=>{

				const item = scroller.querySelector('input:checked')?.closest('.product-variant__item');
				if ( ! item ) return;

				// Runs whenever the row gets or changes its width: first layout, a hidden tab shown, a carousel sizing its cards.
				const observer = new ResizeObserver(()=>{
					if ( ! scroller.clientWidth ) return;
					const row = scroller.getBoundingClientRect();
					const swatch = item.getBoundingClientRect();
					// Half a swatch of room, so the next one peeks out and the row still reads as scrollable.
					const room = swatch.width / 2;
					if ( swatch.right > row.right - room ) {
						scroller.scrollLeft += swatch.right - row.right + room;
					} else if ( swatch.left < row.left + room ) {
						scroller.scrollLeft -= row.left + room - swatch.left;
					}
				});
				observer.observe(scroller);
				this._observers.push(observer);

				const stop = ()=>observer.disconnect();
				['pointerdown', 'wheel', 'touchstart', 'keydown'].forEach(type=>{
					scroller.addEventListener(type, stop, { once: true, passive: true });
				});

			});

		}

		// A card whose badge follows the pick (shop card, or the old card's Saving badge) keeps each colour's or variant's badge in a template (snippets/shop-card-badge-picks.liquid): the picked one goes in, or none without a price or a badge.
		_updateCardBadge(){
			const card = this.closest('[data-js-product-item]');
			const flags = card?.querySelector('[data-shop-card-flags]');
			if ( ! flags ) return;
			const priced = this.querySelector('[data-js-quick-buy-product-price] [data-js-product-price-original]')?.textContent !== '';
			const id = this.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;
			const template = priced && id ? card.querySelector(`template[data-shop-card-badge="${id}"]`) : null;
			flags.querySelector('.shop-card__badge')?.remove();
			if ( template ) flags.append(template.content.cloneNode(true));
		}

		onVariantChangeHandler(e){

			const variant = e.target.currentVariant;

			if ( variant ) {

				if ( this.variantImages ) {
				
					if ( variant.featured_media != null ) {
						const variantImg = this.variantImages.querySelector(`template[data-media-id="${variant.featured_media.id}"]`);
						if ( ! variantImg.dataset.init ) {
							this.initVariantImage(variantImg);
							this.showVariantImage(variant.featured_media.id);
						} else {
							this.showVariantImage(variant.featured_media.id);
						}
					}

				} else if ( this.variantImagesSliders ) {

					if ( variant.featured_image != null && this.productSlider ) {
						const variantImg = this.productSlider.querySelector(`.product-item__image-${variant.featured_image.id}`);
						this.productSlider.changeSlide(variantImg.dataset.index);
					}

				}

			}

		}

		initVariantImage(template) {
			template.dataset.init = true;
			const image = template.content.querySelector('figure').cloneNode(true);
			image.dataset.mediaId = template.dataset.mediaId;
			this.productImage.append(image);
		}

		showVariantImage(id) {
			this.productImage.querySelector('.product-item__image-figure--on-top')?.classList.remove('product-item__image-figure--on-top');
			this.productImage.querySelector(`[data-media-id="${id}"]`).classList.add('product-item__image-figure--on-top');
			this.productImage.querySelector(`[data-media-id="${id}"] img`).srcset = this.productImage.querySelector(`[data-media-id="${id}"] img`).srcset;
			this.productImage.querySelector(`[data-media-id="${id}"]`).style.zIndex = ++this.zIndex;
		}

		_serializeForm(form) {
			// Delegates to the shared serializer in assets/utils.js.
			return window.OrdoUtils.serializeForm(form);
		}

	}

  if ( typeof customElements.get('quick-buy') == 'undefined' ) {
		customElements.define('quick-buy', QuickBuy);
	}

	// Bazaarvoice is loaded once via content_for_header and scans the DOM a single time.
	// The theme editor replaces a section's markup on every edit, which destroys the
	// star ratings BV injected with nothing to bring them back. shopify:section:load
	// fires only in the editor, so this costs the storefront nothing. Guarded, because
	// the BV global and its API depend on the installed app version.
	document.addEventListener('shopify:section:load', ()=>{
		const bv = window.BV || window.$BV;
		try { bv?.ui?.('rr', 'inline_ratings'); } catch (e) { /* BV absent or different API */ }
	});

}

if ( ! window.handleAddToCart ) {

	// Goes through the shared cart queue; the callback gets true when the items were added, so callers can keep their state after a failure.
	window.handleAddToCart = (body, items, headers, callback) => {

		let doNext = false;

		if ( document.body.classList.contains('template-cart' ) ) {
			doNext = "page";
		} else {
			if ( KROWN.settings.cart_popup == "true" && document.getElementById('mini-cart-popup') ) {
				doNext = "popup";
			} else if ( KROWN.settings.cart_action == "overlay" && document.getElementById('site-cart-sidebar') ) {
				doNext = "drawer";
			} else {
				doNext = "page";
			}
		}

		// Errors show in the drawer or the cart page's form; a page without either goes to the cart.
		const cartForm = document.getElementById('AjaxCartForm');
		const drawer = document.getElementById('site-cart-sidebar');

		OrdoUtils.cart.request(`${KROWN.settings.routes.cart_add_url}.js`, body, { headers, section: !! cartForm })
		.then(({ status, message, html }) => {

			if ( ! message && status >= 400 ) {
				throw new Error(`Cart add failed: HTTP ${status}`);
			}

			if ( doNext == "page" && ! ( message && cartForm ) ) {
				window.location.href = KROWN.settings.routes.cart_url;
				return ! message;
			}

			// The items are in the cart, but the drawer can't be redrawn: show the cart page instead.
			if ( cartForm && ! message && ! html ) {
				window.location.href = KROWN.settings.routes.cart_url;
				return true;
			}

			OrdoUtils.cart.render(html);

			if ( message ) {
				OrdoUtils.cart.error(message);
				drawer?.show();
				return false;
			}

			if ( doNext == "drawer" ) {
				drawer.show();
			} else {
				const miniCartPopup = document.getElementById('mini-cart-popup')
				if ( items == 1 ) {
					miniCartPopup.querySelector('[data-js-mini-cart-single]').style.display = 'block';
					miniCartPopup.querySelector('[data-js-mini-cart-plural]').style.display = 'none';
				} else {
					miniCartPopup.querySelector('[data-js-mini-cart-plural]').style.display = 'block';
					miniCartPopup.querySelector('[data-js-mini-cart-plural]').innerHTML = KROWN.settings.locales.cart_added_items_count.replace('{{ count }}', items);
					miniCartPopup.querySelector('[data-js-mini-cart-single]').style.display = 'none';
				}
				miniCartPopup.show();
				if ( miniCartPopup.dataset.hide != '0' ) {
					setTimeout(()=>{
						miniCartPopup.hide();
					}, parseInt(miniCartPopup.dataset.hide));
				}
			}

			return true;

		})
		.catch(e => {
			console.log(e);
			OrdoUtils.cart.error(OrdoUtils.localeText('cart_general_error'));
			drawer?.show();
			return false;
		})
		.then(added => {
			callback(added);
		});

	}

}